/** Matches the backend `kind` column (egpt_schedules). */
export type ScheduleKind = "one_shot" | "cron" | "event";
export type ScheduleEventKind = "dialogue_count" | "user_explicit";

export interface ScheduleSummary {
  id: string;
  name: string;
  kind: ScheduleKind;
  cron_expr: string | null;
  next_run_at: string | null;
  last_run_at: string | null;
  /** Set when the schedule is paused (scheduler skips it). Null = active. */
  paused_at: string | null;
  max_iterations?: number | null;
  max_cost_usd?: number | null;
  created_at: string;
}

export interface Schedule extends ScheduleSummary {
  prompt: string;
  tools: string[];
  last_job_id: string | null;
  updated_at: string;
  event_kind: ScheduleEventKind | null;
  event_threshold: number | null;
}

export interface ScheduleHistoryItem {
  id: string;
  // egpt_jobs statuses.
  status:
    | "pending"
    | "running"
    | "awaiting_approval"
    | "succeeded"
    | "failed"
    | "cancelling"
    | "cancelled";
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  result: string | null;
  /** Set when the job produced an artifact (links to the artifact view). */
  artifact_id?: string | null;
  /** The chat session the scheduled run wrote into, if any. */
  session_id?: string | null;
}

/** Payload for POST /v1/schedules. `kind` drives which timing field is used. */
export interface ScheduleCreateInput {
  name: string;
  kind: ScheduleKind;
  prompt: string;
  /** Tool names the scheduled subagent may use. */
  tools: string[];
  max_iterations?: number;
  max_cost_usd?: number;
  /** IANA timezone the wall-clock schedule is expressed in (browser tz). For
   *  cron, the backend anchors the wall-clock time in this zone so "10 AM"
   *  means 10 AM locally, not UTC. */
  tz?: string;
  // one_shot
  run_at?: string; // ISO-8601 UTC
  // cron
  cron_expr?: string;
  // event
  event_kind?: ScheduleEventKind;
  event_threshold?: number;
}

/** A tool the user can grant a scheduled subagent (from GET /v1/tools). */
export interface ToolInfo {
  name: string;
  description: string;
}

/** One step in a scheduled run's execution trail (egpt_job_events). */
export interface JobEvent {
  seq: number;
  kind: string; // start | tool_use | tool_result | thinking | done | error
  data: Record<string, unknown>;
  ts: string;
}

import { apiFetch } from "@/auth/token";
import { friendlyError } from "@/api/friendlyError";

async function jsonOk<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(friendlyError(res.status));
  return res.json() as Promise<T>;
}

/** Cookie-session request init. Identity rides the HttpOnly `egpt_session`
 *  cookie via apiFetch; no Authorization header. */
function reqInit(extra?: RequestInit): RequestInit {
  return {
    credentials: "include",
    ...extra,
    headers: {
      ...(extra?.headers ?? {}),
    },
  };
}

export async function listSchedules(): Promise<ScheduleSummary[]> {
  const r = await apiFetch(`/v1/schedules`, reqInit());
  const body = await jsonOk<{ schedules: ScheduleSummary[] }>(r);
  return body.schedules;
}

export async function getSchedule(id: string): Promise<Schedule> {
  const r = await apiFetch(`/v1/schedules/${id}`, reqInit());
  return jsonOk<Schedule>(r);
}

export async function cancelSchedule(id: string): Promise<void> {
  const r = await apiFetch(`/v1/schedules/${id}/cancel`, reqInit({ method: "POST" }));
  if (!r.ok) throw new Error(friendlyError(r.status));
}

export async function getScheduleHistory(id: string): Promise<ScheduleHistoryItem[]> {
  const r = await apiFetch(`/v1/schedules/${id}/history`, reqInit());
  const body = await jsonOk<{ jobs: ScheduleHistoryItem[] }>(r);
  return body.jobs;
}

export async function createSchedule(input: ScheduleCreateInput): Promise<Schedule> {
  const r = await apiFetch(
    `/v1/schedules`,
    reqInit({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),
  );
  return jsonOk<Schedule>(r);
}

export async function pauseSchedule(id: string): Promise<void> {
  const r = await apiFetch(`/v1/schedules/${id}/pause`, reqInit({ method: "POST" }));
  if (!r.ok) throw new Error(friendlyError(r.status));
}

export async function resumeSchedule(id: string): Promise<void> {
  const r = await apiFetch(`/v1/schedules/${id}/resume`, reqInit({ method: "POST" }));
  if (!r.ok) throw new Error(friendlyError(r.status));
}

/** Tools available to grant a scheduled subagent (GET /v1/tools). Fail-soft:
 *  returns [] on error so the create form can still render with no picker. */
export async function listTools(): Promise<ToolInfo[]> {
  try {
    const r = await apiFetch(`/v1/tools`, reqInit());
    if (!r.ok) return [];
    const body = (await r.json()) as { tools?: ToolInfo[] };
    return body.tools ?? [];
  } catch {
    return [];
  }
}

/** Step-by-step execution trail for one scheduled run (its job). The history
 *  item's `id` IS the job id. GET /jobs/{job_id}/events. (The jobs router is
 *  mounted at /jobs, not /v1/jobs — nginx passes the path through unrewritten,
 *  so /v1/jobs 404s.) */
export async function getJobEvents(jobId: string): Promise<JobEvent[]> {
  const r = await apiFetch(`/jobs/${jobId}/events`, reqInit());
  const body = await jsonOk<{ events: JobEvent[] }>(r);
  return body.events;
}
